import { UserCheck, UserMinus, UserPlus } from "lucide-react";

import {
  useGetSubscriptionsQuery,
  useSubscribeMutation,
  useUnsubscribeMutation,
} from "@/api/api";
import { HGUser } from "@/common/types";
import { cn } from "@/lib/utils";
import { Button } from "../button";

/**
 * Follow/unfollow another user.
 *
 * Membership is decided on real ids on both sides. Comparing against the URL
 * segment instead — as this once did — silently fails for anyone with a
 * nickname, leaving the button offering to follow someone already followed.
 */
export default function FollowButton({
  user,
  className,
}: {
  user: HGUser;
  className?: string;
}) {
  const { data: subscriptions } = useGetSubscriptionsQuery();
  const [subscribe, { isLoading: subscribing }] = useSubscribeMutation();
  const [unsubscribe, { isLoading: unsubscribing }] = useUnsubscribeMutation();

  const isFollowing = !!subscriptions?.some((sub) => sub.id === user.id);
  const pending = subscribing || unsubscribing;

  const handleClick = () => {
    if (pending) return;
    if (isFollowing) unsubscribe(user.id);
    else subscribe(user.id);
  };

  if (isFollowing) {
    // Label and icon swap on hover so the destructive action is only ever
    // announced under the cursor, while the resting state reads as status.
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={pending}
        aria-label={`Unfollow ${user.name || "user"}`}
        className={cn(
          "group/follow text-[12.5px] hover:border-destructive/40 hover:text-destructive",
          className,
        )}
      >
        <UserCheck className="group-hover/follow:hidden" />
        <UserMinus className="hidden group-hover/follow:block" />
        <span className="group-hover/follow:hidden">Following</span>
        <span className="hidden group-hover/follow:inline">Unfollow</span>
      </Button>
    );
  }

  return (
    <Button
      variant="default"
      size="sm"
      onClick={handleClick}
      disabled={pending}
      aria-label={`Follow ${user.name || "user"}`}
      className={cn("text-[12.5px]", className)}
    >
      <UserPlus />
      Follow
    </Button>
  );
}
